import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Modal } from '@/components/ui/Modal';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  useAssignmentReader,
  useCompleteAssignment,
  useOpenAssignment,
  useSaveAnswer,
  useSaveProgress,
} from '@/hooks/api/useCurate';
import type { AssignmentWithArticle, CurateResponse } from '@/api/curate';
import { CurateBlockView } from './CurateBlock';
import { groupBlocksByPage } from './blocks';
import styles from './CurateReaderModal.module.css';

interface CurateReaderModalProps {
  /** The tile that was clicked. Null closes the reader. */
  assignment: AssignmentWithArticle | null;
  onClose: () => void;
}

/**
 * Paged reader for one assigned Curate article.
 *
 * Reading model, and why it is shaped this way:
 *
 * - The article is split into pages on the server-computed `pageIndex`, and one
 *   page renders at a time. That matches how progress is stored (pages viewed
 *   over page count) so what the learner sees and what is recorded cannot drift.
 *
 * - Position is written on page turn and on close, never continuously. The
 *   schema is explicitly designed for roughly a dozen writes per full read
 *   rather than a per-interaction event stream, so a scroll handler here would
 *   defeat the storage design.
 *
 * - Dwell time is accumulated locally and sent as a delta with each write. The
 *   API adds it to a running total, so sending an absolute would double-count.
 *
 * - A completed assignment opens read-only: answers and grades are visible, but
 *   nothing writes back. Re-reading a finished module should not reopen it or
 *   churn `attemptCount`.
 */
export function CurateReaderModal({ assignment, onClose }: CurateReaderModalProps) {
  const assignmentId = assignment?.assignmentId;
  const open = !!assignment;

  const reader = useAssignmentReader(open ? assignmentId : undefined);
  const openMutation = useOpenAssignment();
  const progressMutation = useSaveProgress();
  const answerMutation = useSaveAnswer(assignmentId);
  const completeMutation = useCompleteAssignment(assignmentId);

  const [pageIndex, setPageIndex] = useState(0);
  const [savingBlockId, setSavingBlockId] = useState<string | null>(null);

  // Wall-clock marker for the current page, used to derive the dwell delta.
  const pageEnteredAt = useRef<number>(Date.now());
  // Guards the once-per-open POST across re-renders and refetches.
  const openedFor = useRef<string | null>(null);
  // Read by the unmount cleanup, which must not re-run when the page changes.
  const latest = useRef({ assignmentId, pageIndex });
  // Top of the reader content; anchor for the page-turn scroll reset.
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const pages = useMemo(
    () => (reader.data ? groupBlocksByPage(reader.data.blocks) : []),
    [reader.data],
  );
  const pageCount = pages.length;
  const article = reader.data?.article;
  const isComplete = reader.data?.assignment.state === 'completed';

  const responsesByBlock = useMemo(() => {
    const map = new Map<string, CurateResponse>();
    for (const r of reader.data?.responses ?? []) map.set(r.blockId, r);
    return map;
  }, [reader.data?.responses]);

  useEffect(() => {
    latest.current = { assignmentId, pageIndex };
  }, [assignmentId, pageIndex]);

  /* Open: fire once per assignment, and only when it can still change state. */
  useEffect(() => {
    if (!open || !assignmentId || !reader.data) return;
    if (openedFor.current === assignmentId) return;
    openedFor.current = assignmentId;
    if (reader.data.assignment.state === 'completed') return;
    openMutation.mutate(assignmentId);
    // openMutation is stable from react-query; excluding it avoids re-firing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, assignmentId, reader.data]);

  /* Resume: land on the saved position the first time the payload arrives. */
  useEffect(() => {
    if (!reader.data) return;
    const saved = reader.data.progress?.lastPageIndex ?? 0;
    const total = groupBlocksByPage(reader.data.blocks).length;
    setPageIndex(Math.min(Math.max(saved, 0), Math.max(total - 1, 0)));
    pageEnteredAt.current = Date.now();
  }, [reader.data]);

  /* Reset per-open state so a second article does not inherit the first's. */
  useEffect(() => {
    if (!open) {
      openedFor.current = null;
      setPageIndex(0);
      setSavingBlockId(null);
    }
  }, [open]);

  const dwellSeconds = useCallback(() => {
    const seconds = Math.round((Date.now() - pageEnteredAt.current) / 1000);
    // Clamp: a modal left open overnight should not report a 12-hour read.
    return Math.min(Math.max(seconds, 0), 3600);
  }, []);

  /**
   * Reset the reader to the top of the new page.
   *
   * The scroll container is the Modal component's own body element, not this
   * component's content div — and the Modal's class names are CSS-module
   * hashed, so it cannot be selected directly. Walk up from the content to the
   * nearest ancestor that actually scrolls and reset that; fall back to
   * scrollIntoView, which scrolls every scrollable ancestor as needed.
   *
   * Focus moves to the content wrapper at the same time (without triggering a
   * second scroll) so keyboard and screen-reader users continue from the top
   * of the new page rather than from a button at the bottom of the old one.
   */
  const scrollToTop = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;

    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior: ScrollBehavior = reduceMotion ? 'auto' : 'smooth';

    let scroller: HTMLElement | null = el.parentElement;
    while (scroller) {
      const overflowY = window.getComputedStyle(scroller).overflowY;
      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        scroller.scrollHeight > scroller.clientHeight
      ) {
        break;
      }
      scroller = scroller.parentElement;
    }

    if (scroller) scroller.scrollTo({ top: 0, behavior });
    else el.scrollIntoView({ block: 'start', behavior });

    el.focus({ preventScroll: true });
  }, []);

  const goToPage = useCallback(
    (next: number) => {
      if (!assignmentId || next === pageIndex) return;
      const bounded = Math.min(Math.max(next, 0), Math.max(pageCount - 1, 0));

      if (!isComplete) {
        // Best-effort: a lost position write costs the learner one page of
        // resume accuracy, which never justifies interrupting the read.
        progressMutation.mutate({
          assignmentId,
          pageIndex: bounded,
          dwellSeconds: dwellSeconds(),
        });
      }

      setPageIndex(bounded);
      pageEnteredAt.current = Date.now();
      // Long pages otherwise leave the reader mid-article after a page turn.
      scrollToTop();
    },
    [assignmentId, pageIndex, pageCount, isComplete, progressMutation, dwellSeconds, scrollToTop],
  );

  /* Close: record the final position. Runs on unmount so it also covers the
   * overlay click, Escape, and the mobile swipe-to-dismiss gesture, none of
   * which route through the footer buttons. */
  useEffect(() => {
    if (!open) return;
    return () => {
      const { assignmentId: id, pageIndex: page } = latest.current;
      if (!id || isComplete) return;
      progressMutation.mutate({
        assignmentId: id,
        pageIndex: page,
        dwellSeconds: Math.min(Math.max(Math.round((Date.now() - pageEnteredAt.current) / 1000), 0), 3600),
        closed: true,
      });
    };
    // Intentionally keyed on `open` only: re-running this on every page change
    // would fire a spurious "closed" write per page turn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleAnswer = useCallback(
    (blockId: string, answer: { selectedOptions?: number[]; freeText?: string }) => {
      if (!assignmentId || isComplete) return;
      setSavingBlockId(blockId);
      answerMutation.mutate(
        { blockId, ...answer },
        { onSettled: () => setSavingBlockId(null) },
      );
    },
    [assignmentId, isComplete, answerMutation],
  );

  const isLastPage = pageIndex >= pageCount - 1;
  const currentPage = pages[pageIndex] ?? [];

  // Progress reflects the furthest page reached, not the current one, so
  // flipping back does not appear to undo work.
  const furthest = Math.max(reader.data?.progress?.furthestPageIndex ?? 0, pageIndex);
  const percent = pageCount > 0 ? Math.round(((furthest + 1) / pageCount) * 100) : 0;

  const title = (
    <h2 id="curate-reader-title" className={styles.title}>
      {article?.title ?? assignment?.article.title ?? 'Article'}
    </h2>
  );

  const subtitle = (() => {
    const parts: string[] = [];
    if (pageCount > 0) parts.push(`Page ${pageIndex + 1} of ${pageCount}`);
    if (isComplete) {
      parts.push(
        reader.data?.assignment.score != null
          ? `Completed · ${reader.data.assignment.score}%`
          : 'Completed',
      );
    } else if (pageCount > 0) {
      // Same rough pace the library uses for its estimates.
      parts.push(`about ${Math.max(pageCount - pageIndex, 1) * 2} min left`);
    }
    if (article?.questionCount) parts.push(`${article.questionCount} questions`);
    return parts.join(' · ');
  })();

  return (
    <Modal
      open={open}
      onClose={onClose}
      labelledBy="curate-reader-title"
      fullScreenOnMobile
      title={title}
      subtitle={subtitle || undefined}
      footer={
        reader.data ? (
          <div className={styles.footer}>
            <button
              type="button"
              className={styles.navButton}
              onClick={() => goToPage(pageIndex - 1)}
              disabled={pageIndex === 0}
            >
              Back
            </button>

            {isLastPage ? (
              isComplete ? (
                <button type="button" className={styles.primaryButton} onClick={onClose}>
                  Close
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.primaryButton}
                  disabled={completeMutation.isPending}
                  onClick={() => completeMutation.mutate(undefined, { onSuccess: onClose })}
                >
                  {completeMutation.isPending ? 'Saving…' : 'Mark complete'}
                </button>
              )
            ) : (
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => goToPage(pageIndex + 1)}
              >
                Next page
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        ) : undefined
      }
    >
      {/* One segment per page: read pages dim, the current page is lit. Replaces
          a single rail so the learner can see how far the article runs. */}
      <div
        className={styles.pages}
        role="progressbar"
        aria-label="Reading progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        {pages.map((_, i) => (
          <span
            key={i}
            className={`${styles.pageSeg} ${i === pageIndex ? styles.pageSegCurrent : i <= furthest ? styles.pageSegSeen : ''}`}
          />
        ))}
      </div>

      {/* tabIndex -1: programmatically focusable so page turns can move focus
          to the top of the new page, without joining the tab order. */}
      <div ref={bodyRef} tabIndex={-1} className={styles.body}>
        {reader.isLoading && (
          <div className={styles.loading} aria-busy="true" aria-label="Loading article">
            <Skeleton variant="text" width="70%" height="1.4em" />
            <Skeleton variant="block" height={14} />
            <Skeleton variant="block" height={14} />
            <Skeleton variant="block" height={14} />
            <Skeleton variant="block" height={120} />
          </div>
        )}

        {reader.isError && (
          <div className={styles.error} role="alert">
            <strong>This article couldn&apos;t be loaded.</strong>
            <p>{(reader.error as Error)?.message ?? 'Please try again.'}</p>
            <button type="button" className={styles.retry} onClick={() => reader.refetch()}>
              Try again
            </button>
          </div>
        )}

        {reader.data && currentPage.length === 0 && (
          <p className={styles.empty}>This page has no content.</p>
        )}

        {reader.data &&
          currentPage.map((block) => (
            <CurateBlockView
              key={block.blockId}
              block={block}
              response={responsesByBlock.get(block.blockId)}
              onAnswer={isComplete ? undefined : handleAnswer}
              saving={savingBlockId === block.blockId}
              locked={isComplete}
            />
          ))}

        {answerMutation.isError && (
          <p className={styles.answerError} role="alert">
            That answer couldn&apos;t be saved. Please try again.
          </p>
        )}

        {completeMutation.isError && (
          <p className={styles.answerError} role="alert">
            Marking this complete didn&apos;t work. Please try again.
          </p>
        )}
      </div>
    </Modal>
  );
}
